/**
 * Theme Variables
 *
 * Centralized color system for the application.
 * Use these variables instead of hardcoding colors throughout the app.
 *
 * Usage in components:
 * - Import: import { colors } from '../theme/variables';
 * - Use: sx={{ color: colors.primary.main }}
 * - Or: sx={{ background: colors.gradients.primary }}
 */

export const colors = {
  // Primary Colors - Cyan/Blue
  primary: {
    main: '#0078D4',      // Darker blue for better contrast on white
    light: '#66F4FF',     // Bright cyan for dark mode
    dark: '#005A9E',      // Even darker for hover states
    contrastText: '#FFFFFF',
    alpha: {
      5: 'rgba(0, 120, 212, 0.05)',
      8: 'rgba(0, 120, 212, 0.08)',
      10: 'rgba(0, 120, 212, 0.10)',
      20: 'rgba(0, 120, 212, 0.20)',
      30: 'rgba(0, 120, 212, 0.30)',
      40: 'rgba(0, 120, 212, 0.40)',
      50: 'rgba(0, 120, 212, 0.50)',
    },
  },

  // Secondary Colors - Purple
  secondary: {
    main: '#9333EA',
    light: '#A855F7',
    dark: '#7E22CE',
    contrastText: '#FFFFFF',
    alpha: {
      5: 'rgba(147, 51, 234, 0.05)',
      8: 'rgba(147, 51, 234, 0.08)',
      10: 'rgba(147, 51, 234, 0.10)',
      20: 'rgba(147, 51, 234, 0.20)',
      30: 'rgba(147, 51, 234, 0.30)',
      40: 'rgba(147, 51, 234, 0.40)',
      50: 'rgba(147, 51, 234, 0.50)',
    },
  },

  // Background Colors
  background: {
    default: '#0A0E1B',      // Deep dark blue - main background
    paper: '#131823',         // Slightly lighter - cards/papers
    elevated: '#1A202E',      // Elevated elements
    overlay: 'rgba(10, 14, 27, 0.8)',  // Semi-transparent overlay
    card: 'rgba(19, 24, 35, 0.6)',     // Glass-morphism cards
  },

  // Text Colors
  text: {
    primary: '#FFFFFF',                    // Primary text
    secondary: 'rgba(255, 255, 255, 0.7)', // Secondary text
    disabled: 'rgba(255, 255, 255, 0.5)',  // Disabled text
    hint: 'rgba(255, 255, 255, 0.4)',      // Hint text
  },

  // Status Colors
  // Status Colors — the one set of cleared / warning / expired hues. These
  // feed the MUI palette (theme.ts) and every status chip, bar and dot, so a
  // coach sees the same green on the roster, the documents tab, the lineup
  // and the parent home. Calm, print-safe hues; the earlier neon set read as
  // a game HUD on white.
  status: {
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#B91C1C',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#B45309',
    },
    success: {
      main: '#16A34A',
      light: '#4ADE80',
      dark: '#15803D',
    },
    info: {
      main: '#0EA5E9',
      light: '#38BDF8',
      dark: '#0369A1',
    },
  },

  // Team surfaces
  team: {
    /** Behind a team cover when the team has set no photo and no color. */
    coverFallback: '#1a1a2e',
  },

  // Grayscale
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  // Whites with Alpha
  white: {
    full: '#FFFFFF',
    alpha: {
      3: 'rgba(255, 255, 255, 0.03)',
      5: 'rgba(255, 255, 255, 0.05)',
      8: 'rgba(255, 255, 255, 0.08)',
      10: 'rgba(255, 255, 255, 0.10)',
      15: 'rgba(255, 255, 255, 0.15)',
      20: 'rgba(255, 255, 255, 0.20)',
      30: 'rgba(255, 255, 255, 0.30)',
      50: 'rgba(255, 255, 255, 0.50)',
      70: 'rgba(255, 255, 255, 0.70)',
      90: 'rgba(255, 255, 255, 0.90)',
    },
  },

  // Blacks with Alpha
  black: {
    full: '#000000',
    alpha: {
      3: 'rgba(0, 0, 0, 0.03)',
      5: 'rgba(0, 0, 0, 0.05)',
      8: 'rgba(0, 0, 0, 0.08)',
      10: 'rgba(0, 0, 0, 0.10)',
      20: 'rgba(0, 0, 0, 0.20)',
      30: 'rgba(0, 0, 0, 0.30)',
      50: 'rgba(0, 0, 0, 0.50)',
      70: 'rgba(0, 0, 0, 0.70)',
      90: 'rgba(0, 0, 0, 0.90)',
    },
  },

  // Borders
  border: {
    default: 'rgba(255, 255, 255, 0.1)',
    light: 'rgba(255, 255, 255, 0.05)',
    medium: 'rgba(255, 255, 255, 0.15)',
    primary: 'rgba(0, 120, 212, 0.3)',
    primaryLight: 'rgba(0, 120, 212, 0.15)',
    divider: 'rgba(255, 255, 255, 0.12)',
  },

  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #0078D4 0%, #005A9E 100%)',
    secondary: 'linear-gradient(135deg, #9333EA 0%, #7E22CE 100%)',
    primaryToSecondary: 'linear-gradient(135deg, #0078D4 0%, #9333EA 100%)',
    dark: 'linear-gradient(180deg, #0A0E1B 0%, #131823 100%)',
    glow: 'radial-gradient(circle, rgba(0, 120, 212, 0.2) 0%, transparent 70%)',
  },

  // Shadows
  shadows: {
    primary: {
      sm: '0 4px 16px rgba(0, 120, 212, 0.3)',
      md: '0 6px 24px rgba(0, 120, 212, 0.4)',
      lg: '0 12px 40px rgba(0, 120, 212, 0.2)',
    },
    secondary: {
      sm: '0 4px 16px rgba(147, 51, 234, 0.3)',
      md: '0 6px 24px rgba(147, 51, 234, 0.4)',
      lg: '0 12px 40px rgba(147, 51, 234, 0.2)',
    },
    dark: {
      sm: '0 2px 8px rgba(0, 0, 0, 0.3)',
      md: '0 4px 16px rgba(0, 0, 0, 0.4)',
      lg: '0 8px 32px rgba(0, 0, 0, 0.5)',
    },
  },

  // Backdrop Filters
  backdrop: {
    blur10: 'blur(10px)',
    blur20: 'blur(20px)',
    blur30: 'blur(30px)',
  },
};

/**
 * Spacing scale (in pixels)
 * Use with MUI's spacing system: sx={{ p: spacing.md }}
 */
export const spacing = {
  xs: 1,    // 8px
  sm: 2,    // 16px
  md: 3,    // 24px
  lg: 4,    // 32px
  xl: 6,    // 48px
  xxl: 8,   // 64px
};

/**
 * Border Radius values
 */
export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
  full: '9999px',
};

/**
 * Z-Index scale
 */
export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  notification: 1080,
};

/**
 * Transition durations (in milliseconds)
 */
export const transitions = {
  fast: 150,
  normal: 300,
  slow: 500,
  slowest: 700,
};

/**
 * Common easing functions
 */
export const easings = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
  decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
};

/**
 * Typography weights
 */
export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

/**
 * Breakpoints (matches MUI defaults)
 */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Export default for convenience
export default {
  colors,
  spacing,
  borderRadius,
  zIndex,
  transitions,
  easings,
  fontWeights,
  breakpoints,
};
